import type { ReactNode } from 'react';

/* ── Design tokens shared across all auth screens (CSS-variable-driven for light/dark) ── */
export const AUTH = {
  pageBg: 'var(--auth-page-bg)',
  cardBg: 'var(--auth-card-bg)',
  cardBorder: 'var(--auth-card-border)',
  cardShadow: 'var(--auth-card-shadow)',
  cardRadius: 16,
  cardMaxW: 480,

  textPrimary: 'var(--auth-text-primary)',
  textSecondary: 'var(--auth-text-secondary)',
  textMuted: 'var(--auth-text-muted)',
  textLabel: 'var(--auth-text-label)',
  textPlaceholder: 'var(--auth-text-placeholder)',
  textError: '#C0533E',
  accent: '#D97645',
  accentDark: '#B85A2A',

  inputBg: 'var(--auth-input-bg)',
  inputBorder: 'var(--auth-input-border)',
  inputFocusBorder: '#D97645',
  inputHeight: 44,
  inputRadius: 8,

  btnHeight: 44,
  btnRadius: 8,
  btnGradient: 'linear-gradient(in oklab 180deg, oklab(66.8% 0.097 0.099) 0%, oklab(59.3% 0.101 0.095) 100%)',
  btnShadow: '#FFFFFF1A 0px 1px 0px inset, #3C230A38 0px 1px 3px',

  sectionBorder: 'var(--auth-section-border)',

  googleBtnBg: 'var(--auth-google-btn-bg)',
} as const;

/* ── Corner Logo (SVG + text) ── */
export function CornerLogo({ size = 28 }: { size?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 256 256" fill="none">
        <defs>
          <linearGradient id="authLogoGrad" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
            <stop stopColor="#D46A3A"/>
            <stop offset="1" stopColor="#8B3A10"/>
          </linearGradient>
        </defs>
        <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#authLogoGrad)" strokeWidth="40" strokeLinecap="round" />
      </svg>
      <span
        style={{
          fontFamily: '"Geist", sans-serif',
          fontSize: 16,
          fontWeight: 600,
          color: AUTH.textPrimary,
          letterSpacing: '-0.01em',
          lineHeight: '20px',
        }}
      >
        Corner
      </span>
    </div>
  );
}

/* ── Liquid glass home button ── */
function HomeButton() {
  return (
    <a
      href="/"
      title="Back to home"
      style={{
        position: 'fixed',
        top: 20,
        left: 20,
        width: 42,
        height: 42,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--auth-home-bg)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid var(--auth-home-border)',
        boxShadow: 'var(--auth-home-shadow)',
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'background 180ms ease, box-shadow 180ms ease',
        zIndex: 10,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '0.85';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '1';
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--auth-home-icon)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9.5L12 3l9 6.5V20a1.5 1.5 0 01-1.5 1.5h-4a1.5 1.5 0 01-1.5-1.5v-4.5h-4V20a1.5 1.5 0 01-1.5 1.5h-4A1.5 1.5 0 013 20z" />
      </svg>
    </a>
  );
}

/* ── Full-page auth layout wrapper ── */
export default function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: AUTH.pageBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Geist", sans-serif',
        padding: 24,
      }}
    >
      <HomeButton />
      {children}
    </div>
  );
}

/* ── Reusable auth card shell ── */
export function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: AUTH.cardMaxW,
        backgroundColor: AUTH.cardBg,
        borderRadius: AUTH.cardRadius,
        border: `1px solid ${AUTH.cardBorder}`,
        boxShadow: AUTH.cardShadow,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

/* ── Primary CTA button ── */
export function PrimaryButton({
  children,
  onClick,
  disabled,
  type = 'button',
  style: extraStyle,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  style?: React.CSSProperties;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        height: AUTH.btnHeight,
        borderRadius: AUTH.btnRadius,
        background: disabled ? '#D9C4B0' : AUTH.btnGradient,
        border: 'none',
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: 600,
        fontFamily: '"Geist", sans-serif',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        boxShadow: disabled ? 'none' : AUTH.btnShadow,
        letterSpacing: '0.01em',
        ...extraStyle,
      }}
    >
      {children}
    </button>
  );
}

/* ── Text input with label ── */
export function AuthInput({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  rightElement,
  autoFocus,
  height,
  radius,
}: {
  label: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  rightElement?: ReactNode;
  autoFocus?: boolean;
  height?: number;
  radius?: number;
}) {
  const h = height ?? AUTH.inputHeight;
  const r = radius ?? AUTH.inputRadius;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <label
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: AUTH.textPrimary,
            fontFamily: '"Geist", sans-serif',
            lineHeight: '16px',
          }}
        >
          {label}
        </label>
        {rightElement}
      </div>
      <div style={{ position: 'relative' }}>
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          style={{
            width: '100%',
            height: h,
            borderRadius: r,
            border: `1px solid ${error ? AUTH.textError : AUTH.inputBorder}`,
            backgroundColor: error ? 'var(--auth-error-input-bg)' : AUTH.inputBg,
            padding: '0 12px',
            fontSize: 14,
            fontWeight: 400,
            fontFamily: '"Geist", sans-serif',
            color: AUTH.textPrimary,
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = AUTH.inputFocusBorder;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = error ? AUTH.textError : AUTH.inputBorder;
          }}
        />
      </div>
      {error && (
        <span style={{ fontSize: 12, color: AUTH.textError, fontWeight: 400 }}>{error}</span>
      )}
    </div>
  );
}

/* ── Google OAuth button ── */
export function GoogleButton({
  onClick,
  height = 42,
  radius = 8,
  bg = AUTH.googleBtnBg,
}: {
  onClick: () => void;
  height?: number;
  radius?: number;
  bg?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        height,
        borderRadius: radius,
        backgroundColor: bg,
        border: `1px solid ${AUTH.cardBorder}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        cursor: 'pointer',
        fontFamily: '"Geist", sans-serif',
        fontSize: 13,
        fontWeight: 500,
        color: AUTH.textPrimary,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 18 18">
        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
        <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 3.58z" fill="#EA4335" />
      </svg>
      Continue with Google
    </button>
  );
}

/* ── "or" divider ── */
export function OrDivider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ flex: 1, height: 1, backgroundColor: AUTH.cardBorder }} />
      <span style={{ fontSize: 12, color: AUTH.textSecondary, fontWeight: 400 }}>or</span>
      <div style={{ flex: 1, height: 1, backgroundColor: AUTH.cardBorder }} />
    </div>
  );
}

/* ── Link-style text button ── */
export function AuthLink({
  children,
  onClick,
  color = AUTH.accent,
  weight = 500,
  size = 12,
}: {
  children: ReactNode;
  onClick: () => void;
  color?: string;
  weight?: number;
  size?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        fontSize: size,
        fontWeight: weight,
        color,
        fontFamily: '"Geist", sans-serif',
        textDecoration: 'underline',
        textUnderlineOffset: 2,
      }}
    >
      {children}
    </button>
  );
}
